import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, RadioButtonGroup, Icon } from 'components/patterns'

import strings from 'utils/strings'
import { classNames } from 'utils/utils'
import {
  INPUT_CONFIG_RANGES,
  DIGITAL_INPUT,
  ANALOG_INPUT,
} from 'utils/constants'
import { configurableInputValueLabel } from 'components/feature/utils'
import { StyledRadioGroup } from './InputConfigEditor.style'

const InputConfigEditor = ({
  className,
  onInputConfigChange,
  inputConfig,
  dataType,
  selectedInputDescription,
  disabled,
  requirePrompt,
  disabledConfigs,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [transitionalValue, setTransitionalValue] = useState(null)
  const [selectedValue, setSelectedValue] = useState(inputConfig)

  // Sync selectedValue when inputConfig changes externally (e.g. pin change updates polarity)
  useEffect(() => {
    setSelectedValue(inputConfig)
  }, [inputConfig])

  const maybeChangeOrPrompt = ({ target: { value } }) => {
    if (value !== selectedValue) {
      setSelectedValue(value)
      if (requirePrompt) {
        setTransitionalValue(value)
        setModalIsOpen(true)
      } else {
        onInputConfigChange(value)
      }
    }
  }
  const confirm = () => {
    onInputConfigChange(transitionalValue)
    setSelectedValue(transitionalValue)
    setModalIsOpen(false)
    setTransitionalValue(null)
  }

  const closeModal = () => {
    setModalIsOpen(false)
    setSelectedValue(inputConfig)
    setTransitionalValue(null)
  }

  const header = (selectedInputDescriptionValue) => (
    <div>
      <Icon
        icon="alert_exclamation_triangle_solid"
        className="modal-header-icon"
      />
      {strings.configurable_input_change_warn_header_text(
        selectedInputDescriptionValue
      )}
    </div>
  )

  const menuOptions = INPUT_CONFIG_RANGES[dataType].map((option) => ({
    key: option,
    value: option,
    text: configurableInputValueLabel(option),
    disabled: disabledConfigs.includes(option),
  }))

  const label =
    dataType === DIGITAL_INPUT ? strings.polarity : strings.measure_label

  const renderGroupOptions = () => {
    if (dataType === ANALOG_INPUT) {
      return (
        <StyledRadioGroup
          label={label}
          options={menuOptions}
          value={selectedValue}
          onChange={maybeChangeOrPrompt}
          radioGroupClassName="measure-radio-group"
        />
      )
    }

    return (
      <RadioButtonGroup
        label={label}
        currentValue={selectedValue}
        onChange={maybeChangeOrPrompt}
        buttons={menuOptions}
        disabled={disabled}
        vertical
        radioGroupClassName="polarity-radio-group"
      />
    )
  }

  return (
    <div className={classNames(className, 'input-config-editor')}>
      {renderGroupOptions()}
      <Modal
        header={header(selectedInputDescription)}
        isOpen={modalIsOpen}
        yesHandler={confirm}
        noHandler={closeModal}
        noButtonText={strings.cancel}
        yesButtonText={strings.configurable_input_confirm_button_text}
        wrapClassName="update-all-instances-modal"
      >
        <p>
          {strings.configurable_input_change_warn(selectedInputDescription)}
        </p>
      </Modal>
    </div>
  )
}

InputConfigEditor.defaultProps = {
  disabled: false,
}

InputConfigEditor.propTypes = {
  className: PropTypes.string,
  onInputConfigChange: PropTypes.func,
  inputConfig: PropTypes.string,
  selectedInputDescription: PropTypes.string,
  dataType: PropTypes.string,
  disabled: PropTypes.bool,
  requirePrompt: PropTypes.bool,
  disabledConfigs: PropTypes.array,
}

export default InputConfigEditor
